import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db70dhcjfl1ikpv8spng';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      formEl.reset();
      return;
    }

    formData.delete('website_alt');
    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, String(value)));

    setStatus('loading');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const lowered = String(serverMsg).toLowerCase();
      const isSpam = lowered.includes('spam') || lowered.includes('form data is spam');
      const success = response.ok && parsed?.code === 'OK' && !isSpam;

      if (success) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后再试');
    }
  };

  const inputClass =
    'h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400';

  return (
    <form
      id="contact-form-about"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
        <i className="ri-mail-send-line text-primary-500"></i> 给我们留言
      </h2>
      <p className="mt-1 text-sm text-foreground-500">有任何建议或合作意向，欢迎随时联系我们。</p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-700">称呼</label>
          <input id="contact-name" name="name" required placeholder="请输入您的称呼" className={inputClass} />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-700">邮箱</label>
          <input id="contact-email" name="email" type="email" required placeholder="用于接收回复" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-subject" className="mb-1.5 block text-sm text-foreground-700">主题</label>
        <select id="contact-subject" name="subject" required defaultValue="" className={`${inputClass} cursor-pointer`}>
          <option value="" disabled>请选择留言主题</option>
          <option value="内容建议">内容建议</option>
          <option value="资源反馈">资源反馈</option>
          <option value="商务合作">商务合作</option>
          <option value="版权相关">版权相关</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="mb-1.5 block text-sm text-foreground-700">留言内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请输入留言内容（不超过 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 p-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-guard-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === 'loading'}
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 disabled:opacity-60"
        >
          <i className="ri-send-plane-fill"></i>
          {status === 'loading' ? '提交中...' : '提交留言'}
        </button>
        {status === 'success' && (
          <p className="flex items-center gap-1.5 text-sm text-secondary-700">
            <i className="ri-checkbox-circle-fill text-lg"></i> 提交成功，感谢你的留言！
          </p>
        )}
        {status === 'error' && (
          <p className="flex items-center gap-1.5 text-sm text-accent-700">
            <i className="ri-error-warning-fill text-lg"></i> {formError}
          </p>
        )}
      </div>
    </form>
  );
}